import React from "react";
import SiteHeader from "../components/SiteHeader";
import Reveal from "../components/Reveal";
import { IconArrowRight, IconCheck, IconErrand } from "../components/Icons";
import { RIDER_AREA, RIDER_DUTIES, RIDER_REQUIREMENTS, RIDER_TERMS, RIDER_VERIFICATION } from "../data/riders";
import { useDocumentMeta } from "../useDocumentMeta";
import "../styles/contact.css";
import "../styles/apply.css";

/* One static opening for now. Applying goes to the short form on /riders. */
const JobsPage: React.FC = () => {
  useDocumentMeta({
    title: "Jobs at PABS — Delivery rider",
    description: `PABS is looking for delivery rider partners in ${RIDER_AREA}. Deliver food and groceries and run Pasugo errands, going online when you choose.`,
    canonicalPath: "/jobs",
    ogImage: "https://pabs.online/logo512.png",
    jsonLdId: "pabs-jobs-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: "Delivery rider",
      description:
        "Deliver food, grocery, and pharmacy orders and run Pasugo errands for customers using the PABS app.",
      datePosted: "2026-09-30",
      // Riders are independent partners under the rider agreement.
      employmentType: "CONTRACTOR",
      hiringOrganization: {
        "@type": "Organization",
        name: "PABS",
        sameAs: "https://pabs.online/",
        logo: "https://pabs.online/logo512.png",
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressRegion: RIDER_AREA,
          addressCountry: "PH",
        },
      },
    },
  });

  return (
    <>
      <SiteHeader current="/jobs" />

      <main id="main" className="ct">
        <section className="ct__hero">
          <div className="ct__bg" aria-hidden="true" />
          <div className="u-container">
            <Reveal className="ct__head">
              <span className="t-eyebrow">Jobs</span>
              <h1 className="t-h1 ct__title">
                Work with <span className="t-grad">PABS.</span>
              </h1>
              <p className="t-lead ct__lead">
                We're a local team building deliveries, errands, and rides for{" "}
                {RIDER_AREA}. Here's who we're looking for right now.
              </p>
            </Reveal>

            <Reveal delay={80}>
              <article className="job" aria-labelledby="job-delivery-rider">
                <header className="job__head">
                  <div>
                    <div className="job__title">
                      <span className="icon-tile icon-tile--violet">
                        <IconErrand size={22} />
                      </span>
                      <h2 className="t-h3" id="job-delivery-rider">
                        Delivery rider
                      </h2>
                    </div>
                    <div className="job__meta">
                      <span className="badge">{RIDER_AREA}</span>
                      <span className="badge badge--neutral">Independent partner</span>
                      <span className="badge badge--neutral">Go online when you choose</span>
                    </div>
                  </div>
                  <a className="btn btn--primary btn--lg" href="/riders#apply">
                    Apply now
                    <IconArrowRight size={18} className="btn__icon btn__icon--arrow" />
                  </a>
                </header>

                <div className="job__body">
                  <div className="job__about">
                    <h3>About the role</h3>
                    <p>
                      PABS riders deliver meals, groceries, and pharmacy items,
                      and run Pasugo errands for customers around town. You work
                      as an independent rider partner: go online in the PABS
                      rider app when you're ready, accept the bookings that come
                      in, and customers pay in cash or by GCash.
                    </p>
                  </div>

                  <div>
                    <h3>What you'll do</h3>
                    <ul className="ap__list">
                      {RIDER_DUTIES.map((d) => (
                        <li key={d}>
                          <IconCheck size={16} />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3>Requirements</h3>
                    <ul className="ap__list">
                      {RIDER_REQUIREMENTS.map((r) => (
                        <li key={r}>
                          <IconCheck size={16} />
                          {r}
                        </li>
                      ))}
                    </ul>
                    <p className="ap__note">
                      No documents to upload here. Leave your name and number,
                      and bring these when we meet. {RIDER_VERIFICATION}
                    </p>
                  </div>

                  <div className="job__about">
                    <h3>Good to know</h3>
                    <ul className="ap__list">
                      {RIDER_TERMS.map((t) => (
                        <li key={t}>
                          <IconCheck size={16} />
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            </Reveal>
          </div>
        </section>
      </main>
    </>
  );
};

export default JobsPage;
